import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Spin, Typography } from "antd";
import moment from "moment";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { fetchCompanyPolicyPdfBlob } from "../../../app/utils/common/companyPolicyPdf";
import { APIS } from "../../../app/utils/constants/apiConstant";
import {
  checkPermission,
  checkAnyPermission,
  permissionType,
  showErrorResponse,
} from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import CompanyPolicyPdfViewer from "../../molecules/CompanyPolicyPdfViewer/CompanyPolicyPdfViewer";

const { Title, Text } = Typography;

const CompanyPolicyPage = () => {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [titleInput, setTitleInput] = useState("Company policy");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState(null);
  const fileInputRef = useRef(null);

  const canRead = checkPermission(permissionType.COMPANY_POLICY, "read");
  const canCreate = checkPermission(permissionType.COMPANY_POLICY, "create");
  const canUpdate = checkPermission(permissionType.COMPANY_POLICY, "update");
  const canDelete = checkPermission(permissionType.COMPANY_POLICY, "delete");
  const canManageUpload = checkAnyPermission(permissionType.COMPANY_POLICY, [
    "create",
    "update",
  ]);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.COMPANY_POLICY,
        method: "GET",
      });
      const d = response?.data?.data;
      setMeta(d || null);
      if (d?.title) setTitleInput(d.title);
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) loadMeta();
  }, [canRead, loadMeta]);

  const loadPdf = useCallback(async () => {
    if (!meta?.hasFile) {
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    setPdfLoading(true);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const blob = await fetchCompanyPolicyPdfBlob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      showErrorToast(error?.message || "Unable to display the policy.");
    } finally {
      setPdfLoading(false);
    }
  }, [meta?.hasFile]);

  useEffect(() => {
    if (!canRead || !meta?.hasFile) return undefined;
    loadPdf();
    return () => {
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [canRead, meta?.hasFile, loadPdf]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    setFileList(f || null);
  };

  const submitUpload = async (method) => {
    if (!fileList) {
      showErrorToast("Choose a PDF file first.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", fileList);
      formData.append("title", titleInput.trim() || "Company policy");
      await protectedApi({
        url: APIS.COMPANY_POLICY,
        method,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        _dataEncrypted: true,
      });
      showSuccessToast(
        method === "POST" ? "Company policy uploaded." : "Company policy updated."
      );
      setFileList(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadMeta();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    setUploading(true);
    try {
      await protectedApi({ url: APIS.COMPANY_POLICY, method: "DELETE" });
      showSuccessToast("Company policy removed.");
      await loadMeta();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setUploading(false);
    }
  };

  if (!canRead) {
    return (
      <div className="p-6">
        <Text type="danger">You do not have permission to view the company policy.</Text>
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto flex flex-col gap-6">
      <div>
        <Title level={3} className="!mb-1">
          Company policy
        </Title>
        <Text type="secondary">
        </Text>
      </div>

      {canManageUpload && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
          <div className="font-medium text-[#27314a]">Admin — upload or replace (PDF only)</div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <div className="text-sm mb-1">Title</div>
              <Input
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                placeholder="e.g. Employee handbook 2026"
              />
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={onPickFile}
                className="text-sm"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canCreate && !meta?.hasFile && (
              <Button
                type="primary"
                loading={uploading}
                onClick={() => submitUpload("POST")}
                disabled={!fileList}
              >
                Upload policy
              </Button>
            )}
            {canUpdate && meta?.hasFile && (
              <Button
                type="primary"
                loading={uploading}
                onClick={() => submitUpload("PUT")}
                disabled={!fileList}
              >
                Replace policy
              </Button>
            )}
            {canDelete && meta?.hasFile && (
              <Button danger loading={uploading} onClick={onDelete}>
                Remove policy
              </Button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white min-h-[480px] flex flex-col print:hidden">
          <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
            <span className="font-medium text-[#27314a]">
              {meta?.title || "Company policy"}
            </span>
            {/* {meta?.updatedAt && (
              <Text type="secondary" className="text-xs">
                Updated {moment(meta.updatedAt).format("MMM D, YYYY h:mm A")}
              </Text>
            )} */}
          </div>
          <div className="flex-1 relative bg-[#f5f5f5] min-h-[420px]">
            {!meta?.hasFile && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-gray-500">
                No policy document has been published yet.
              </div>
            )}
            {meta?.hasFile && pdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Spin />
              </div>
            )}
            {meta?.hasFile && pdfUrl && !pdfLoading && (
              <div
                className="overflow-y-auto max-h-[min(70vh,780px)] p-3"
                onDragStart={(e) => e.preventDefault()}
              >
                <CompanyPolicyPdfViewer
                  key={pdfUrl}
                  fileUrl={pdfUrl}
                  onLoadError={() =>
                    showErrorToast("Could not display the policy document. Try again or contact support.")
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPolicyPage;
